// Guest-review numbers: one shape for every surface that prints them.
//
// The rating and count on /stay, the /stay card, the JSON-LD, the prerendered
// HTML and /api/stay all read property.reviews. That is the hand-entered block
// in property-config.js unless the build synced newer numbers from OwnerRez
// (scripts/sync-reviews.mjs), in which case those are laid over it. These
// helpers are shared by that build step, the functions that read OwnerRez,
// and the pages that print the result.

// OwnerRez names the channel each review was posted to. A name we do not
// recognise passes through as written rather than being guessed into one.
export function siteLabel(listingSite) {
  const s = String(listingSite || '').trim();
  if (!s || /ownerrez|direct/i.test(s)) return 'Direct';
  if (/airbnb/i.test(s)) return 'Airbnb';
  if (/vrbo|homeaway/i.test(s)) return 'Vrbo';
  if (/booking\.com/i.test(s)) return 'Booking.com';
  return s;
}

const round2 = (n) => Math.round(n * 100) / 100;

// The property's rating is guest reviews of the property, with stars, that
// are visible where they were posted. A host's review of a guest is not part
// of it, and neither is a hidden review. OwnerRez gives `stars` out of five
// for every channel, so the channels average together without rescaling.
export function summarizeReviews(items, propertyId) {
  const rated = (items || [])
    .filter(
      (r) =>
        r &&
        r.reviewer !== 'host' &&
        r.visible !== false &&
        (propertyId == null || String(r.property_id) === String(propertyId))
    )
    .map((r) => ({ ...r, stars: Number(r.stars) }))
    .filter((r) => r.stars >= 1 && r.stars <= 5);

  if (!rated.length) return { count: 0, rating: null, sites: [], latestReviewDate: null };

  const bySite = new Map();
  for (const r of rated) {
    const name = siteLabel(r.listing_site);
    const entry = bySite.get(name) || { name, count: 0, total: 0 };
    entry.count += 1;
    entry.total += r.stars;
    bySite.set(name, entry);
  }
  const total = rated.reduce((sum, r) => sum + r.stars, 0);
  const dates = rated.map((r) => String(r.date || r.created_utc || '').slice(0, 10)).filter(Boolean).sort();

  return {
    count: rated.length,
    rating: round2(total / rated.length),
    sites: [...bySite.values()]
      .sort((a, b) => b.count - a.count)
      .map(({ name, count, total: siteTotal }) => ({ name, count, rating: round2(siteTotal / count) })),
    latestReviewDate: dates.length ? dates[dates.length - 1] : null
  };
}

// Whether synced numbers may replace the hand-entered ones. The hand-entered
// count is a floor: OwnerRez can lag a channel's import, or hold only the
// reviews written since the channel was connected, and quietly printing a
// smaller number would understate the home. A review a channel removed is the
// one honest reason for a drop; a person accepts it by lowering the floor.
export function checkSync(floor, fresh) {
  if (!fresh?.count) return { ok: false, reason: 'OwnerRez returned no rated guest reviews' };
  if (!(fresh.rating >= 1 && fresh.rating <= 5)) {
    return { ok: false, reason: `the rating it computed (${fresh.rating}) is outside 1 to 5` };
  }
  if (floor?.count && fresh.count < floor.count) {
    return {
      ok: false,
      reason: `OwnerRez has ${fresh.count} reviews, fewer than the ${floor.count} entered by hand in property-config.js`
    };
  }
  return { ok: true };
}

const sitesKey = (r) => (r?.sites || []).map((s) => `${s.name}:${s.count}:${s.rating}`).join('|');

// Same figures, so nothing on the page would change.
export function sameNumbers(a, b) {
  return a?.count === b?.count && a?.rating === b?.rating && sitesKey(a) === sitesKey(b);
}

// "on Airbnb", "on Airbnb and Vrbo", "on Airbnb, Vrbo and direct bookings".
// Names only the channels that have a review, so the page never claims
// reviews on a site where there are none.
export function reviewSitesPhrase(reviews) {
  const names = (reviews?.sites || [])
    .filter((s) => s.count > 0)
    .map((s) => (s.name === 'Direct' ? 'direct bookings' : s.name));
  if (!names.length) return '';
  if (names.length === 1) return `on ${names[0]}`;
  return `on ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
